/**
 * @module utils/referral
 * @description Lightweight, frontend-only referral capture & share helpers.
 *
 * Flow (Yuri spec 2026-02 Iter. 311):
 *   1. Visitor lands on /presentation?ref=ABC123 → captureRefFromUrl() stores
 *      `ABC123` in localStorage under `yumix_referrer` (persistent until
 *      sign-up completes or 30 days, whichever comes first).
 *   2. Presentation page displays an "🎁 Invité par X" ribbon if a referrer
 *      is present in localStorage.
 *   3. Logged-in users can call buildShareUrl(userId) to get
 *      `https://...preview.../presentation?ref=<userId>` and use the Web
 *      Share API (mobile native share sheet) or fallback to clipboard.
 *
 * Backend hookup (TODO, not in this iteration): on user registration, the
 * frontend should pass `referred_by` from localStorage in the auth payload,
 * and the backend should award +50 Éclaireur points to the referrer.
 */

const STORAGE_KEY = 'yumix_referrer';
const STORAGE_KEY_TS = 'yumix_referrer_ts';
const TTL_DAYS = 30;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

// ── Capture ──

/**
 * Reads `?ref=` from the current URL and stores it in localStorage.
 * Should be called on mount of public landing surfaces (Presentation, Landing).
 * Idempotent: if the same ref is already stored, only the timestamp is refreshed.
 * Skips if no `?ref=` present.
 */
export function captureRefFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get('ref') || '').trim();
    if (!ref) return getStoredRef(); // honor existing ref if no new one
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(ref)) return getStoredRef(); // safety
    localStorage.setItem(STORAGE_KEY, ref);
    localStorage.setItem(STORAGE_KEY_TS, String(Date.now()));
    return ref;
  } catch (_) {
    return null;
  }
}

/**
 * Returns the currently-stored referrer (if any) and clears it if expired.
 */
export function getStoredRef() {
  if (typeof window === 'undefined') return null;
  try {
    const ref = localStorage.getItem(STORAGE_KEY);
    if (!ref) return null;
    const ts = Number(localStorage.getItem(STORAGE_KEY_TS) || '0');
    if (ts && Date.now() - ts > TTL_MS) {
      clearStoredRef();
      return null;
    }
    return ref;
  } catch (_) {
    return null;
  }
}

export function clearStoredRef() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY_TS);
  } catch (_) { /* noop */ }
}

// ── Share ──

/**
 * Builds the public share URL for the current host with a `?ref=<id>` param.
 * Defaults to /presentation as the share target — the most polished public
 * surface to convert curious friends.
 */
export function buildShareUrl(refId, target = '/presentation') {
  if (typeof window === 'undefined') return '';
  const id = encodeURIComponent(String(refId || '').trim());
  if (!id) return `${window.location.origin}${target}`;
  return `${window.location.origin}${target}?ref=${id}`;
}

/**
 * Shares the URL via the Web Share API (mobile native sheet) or falls back
 * to copying the URL to the clipboard.
 *
 * @returns {Promise<{ok: boolean, method: 'share'|'clipboard'|'none'}>}
 */
export async function shareYumix({ refId, fr = true, target = '/presentation' } = {}) {
  const url = buildShareUrl(refId, target);
  const title = 'YUMIX';
  const text = fr
    ? `Je teste YUMIX — coach nutrition + performance, photo de l'assiette, IA proactive. Tu devrais regarder.`
    : `I'm testing YUMIX — nutrition + performance coach, plate photo, proactive AI. You should check it out.`;
  // Web Share API (most mobile browsers + Safari desktop)
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text, url });
      return { ok: true, method: 'share' };
    } catch (e) {
      if (e?.name === 'AbortError') return { ok: false, method: 'share' };
      // fall through to clipboard
    }
  }
  // Clipboard fallback
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(url);
      return { ok: true, method: 'clipboard' };
    } catch (_) { /* noop */ }
  }
  return { ok: false, method: 'none' };
}
